'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetUserActivityRow, type ItemsRenderProps, useGetUserActivityField, type useGetUserActivityInfinite } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type Ref, useEffect } from 'react';
import { ActivityEmpty } from '@/components/portfolio/empty-states/activity-empty';
import { InfiniteList } from '@/components/ui/infinite-list';
import { RetryButton } from '@/components/ui/retry-button';
import { logger } from '@/lib/telemetry/logger';
import { isActivityAttributedToWallet } from '@/lib/wallet/multi-wallet-scope';
import { ActivityRow, ActivityRowSkeleton } from './portfolio-activity-feed-row';
import { type ActivityFilter, matchesActivityFilter } from './portfolio-activity-filter';
interface PortfolioActivityFeedProps {
  filter?: ActivityFilter | undefined;
  query: ReturnType<typeof useGetUserActivityInfinite>;
  isOwn: boolean;
  attributionWalletAddress: string | null;
}
export function PortfolioActivityFeed(t0) {
  const $ = _c(59);
  const {
    query,
    isOwn,
    attributionWalletAddress,
    filter
  } = t0;
  const t = useTranslations("profile.stats");
  const visibleRows = filterActivityRows(query.rows, attributionWalletAddress, filter);
  const hasVisibleRows = visibleRows.length > 0;
  let t1;
  let t2;
  if ($[0] !== filter || $[1] !== query.isError || $[2] !== query.nextPageError) {
    t1 = () => {
      if (query.isError) {
        logger.warn("portfolio.activity.load_failed", {
          filter
        });
      }
      if (query.nextPageError !== null) {
        logger.warn("portfolio.activity.page_failed", {
          filter
        });
      }
    };
    t2 = [query.isError, query.nextPageError, filter];
    $[0] = filter;
    $[1] = query.isError;
    $[2] = query.nextPageError;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== filter || $[6] !== query) {
    t3 = async function loadMore() {
      try {
        await query.fetchNextPage();
      } catch {
        logger.warn("portfolio.activity.page_failed", {
          filter
        });
      }
    };
    $[5] = filter;
    $[6] = query;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const loadMore = t3;
  if (query.isPending) {
    let t4;
    if ($[8] !== t) {
      t4 = t("activity_loading");
      $[8] = t;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t4) {
      t5 = <output className="sr-only">{t4}</output>;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <ul className="m-0 flex list-none flex-col p-0" data-testid="activity-feed-skeleton"><ActivityRowSkeleton /><ActivityRowSkeleton /><ActivityRowSkeleton /></ul>;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== t5) {
      t7 = <>{t5}{t6}</>;
      $[13] = t5;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  if (query.isError && !hasVisibleRows) {
    let t4;
    if ($[15] !== t) {
      t4 = t("activity_error_title");
      $[15] = t;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t5;
    if ($[17] !== t4) {
      t5 = <h3 className="text-heading-md">{t4}</h3>;
      $[17] = t4;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== t) {
      t6 = t("activity_error_body");
      $[19] = t;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t7;
    if ($[21] !== t6) {
      t7 = <p className="max-w-sm text-body text-foreground-secondary">{t6}</p>;
      $[21] = t6;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t8;
    if ($[23] !== query) {
      t8 = () => {
        query.refetch();
      };
      $[23] = query;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    let t9;
    if ($[25] !== t) {
      t9 = t("activity_retry");
      $[25] = t;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    let t10;
    if ($[27] !== t8 || $[28] !== t9) {
      t10 = <RetryButton onRetry={t8}>{t9}</RetryButton>;
      $[27] = t8;
      $[28] = t9;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    let t11;
    if ($[30] !== t10 || $[31] !== t5 || $[32] !== t7) {
      t11 = <div className="flex flex-col items-center justify-center gap-3 py-12 text-center" data-testid="activity-feed-error">{t5}{t7}{t10}</div>;
      $[30] = t10;
      $[31] = t5;
      $[32] = t7;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    return t11;
  }
  if (!hasVisibleRows && query.hasNextPage !== true) {
    let t4;
    if ($[34] !== filter) {
      t4 = <ActivityEmpty filter={filter} />;
      $[34] = filter;
      $[35] = t4;
    } else {
      t4 = $[35];
    }
    return t4;
  }
  let t4;
  if ($[36] !== query.isFetchingNextPage || $[37] !== t) {
    t4 = query.isFetchingNextPage ? <output className="sr-only">{t("activity_loading_older")}</output> : null;
    $[36] = query.isFetchingNextPage;
    $[37] = t;
    $[38] = t4;
  } else {
    t4 = $[38];
  }
  const t5 = query.hasNextPage === true && query.nextPageError === null;
  let t6;
  if ($[39] !== attributionWalletAddress || $[40] !== filter || $[41] !== isOwn) {
    t6 = t7 => {
      const {
        ref,
        id
      } = t7;
      return <AttributedActivityRow ref={ref} rowId={id} filter={filter} isOwn={isOwn} attributionWalletAddress={attributionWalletAddress} />;
    };
    $[39] = attributionWalletAddress;
    $[40] = filter;
    $[41] = isOwn;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  let t7;
  if ($[43] !== query.Items || $[44] !== t6) {
    t7 = <ul className="m-0 flex list-none flex-col p-0"><query.Items>{t6}</query.Items></ul>;
    $[43] = query.Items;
    $[44] = t6;
    $[45] = t7;
  } else {
    t7 = $[45];
  }
  let t8;
  if ($[46] !== loadMore || $[47] !== query.isFetchingNextPage || $[48] !== t5 || $[49] !== t7) {
    t8 = <InfiniteList hasNextPage={t5} isFetchingNextPage={query.isFetchingNextPage} fetchNextPage={loadMore} scrollMode="page-below-lg">{t7}</InfiniteList>;
    $[46] = loadMore;
    $[47] = query.isFetchingNextPage;
    $[48] = t5;
    $[49] = t7;
    $[50] = t8;
  } else {
    t8 = $[50];
  }
  let t9;
  if ($[51] !== filter || $[52] !== query || $[53] !== t) {
    t9 = query.nextPageError !== null ? <div className="flex items-center justify-center gap-3 py-3 text-center" data-testid="activity-feed-pagination-error" aria-live="polite"><p className="text-caption text-foreground-secondary">{t("activity_older_error")}</p><RetryButton variant="tertiary" size="sm" onRetry={() => {
        query.retryNextPage().catch(() => {
          logger.warn("portfolio.activity.retry_failed", {
            filter
          });
        });
      }}>{t("activity_retry")}</RetryButton></div> : null;
    $[51] = filter;
    $[52] = query;
    $[53] = t;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  let t10;
  if ($[55] !== t4 || $[56] !== t8 || $[57] !== t9) {
    t10 = <>{t4}{t8}{t9}</>;
    $[55] = t4;
    $[56] = t8;
    $[57] = t9;
    $[58] = t10;
  } else {
    t10 = $[58];
  }
  return t10;
}
function filterActivityRows(rows: readonly GetUserActivityRow[], attributionWalletAddress: string | null, filter: ActivityFilter | undefined): readonly GetUserActivityRow[] {
  return rows.filter(row => matchesActivityFilter(row, filter) && (attributionWalletAddress === null || isActivityAttributedToWallet(row, attributionWalletAddress)));
}
function AttributedActivityRow(t0) {
  const $ = _c(6);
  const {
    ref,
    rowId,
    isOwn,
    attributionWalletAddress,
    filter
  } = t0;
  const kind = useGetUserActivityField(rowId, "kind");
  const side = useGetUserActivityField(rowId, "side");
  const trader = useGetUserActivityField(rowId, "trader") as string | undefined ?? null;
  const participationId = useGetUserActivityField(rowId, "participationId") as string | undefined ?? null;
  if (!matchesActivityFilter({
    kind,
    side
  }, filter) || attributionWalletAddress !== null && !isActivityAttributedToWallet({
    trader,
    participationId
  }, attributionWalletAddress)) {
    let t1;
    if ($[0] !== ref) {
      t1 = <li ref={ref} hidden={true} aria-hidden="true" />;
      $[0] = ref;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== isOwn || $[3] !== ref || $[4] !== rowId) {
    t1 = <ActivityRow ref={ref} rowId={rowId} isOwn={isOwn} />;
    $[2] = isOwn;
    $[3] = ref;
    $[4] = rowId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}